import { createTheme } from '@mui/material/styles';

// Modern bookish theme with enhanced design tokens
const bookishTheme = createTheme({
  palette: {
    primary: {
      main: '#0B0E26', // Deep navy with slight warmth
      light: '#1A1F42', // Refined lighter navy
      dark: '#050711', // Deeper navy
      contrastText: '#FDFCF8', // Warmer off-white
    },
    secondary: {
      main: '#C9A961', // Sophisticated gold
      light: '#E8D5A3', // Lighter gold with warmth
      dark: '#9A7B3A', // Deeper gold
      contrastText: '#0B0E26', // Navy for contrast
    },
    background: {
      default: '#0B0E26', // Navy background
      paper: '#141832', // Elevated surface
    },
    text: {
      primary: '#FDFCF8', // Warmer off-white
      secondary: '#C9A961', // Refined gold
    },
    divider: 'rgba(201, 169, 97, 0.2)', // Subtle gold divider
    info: {
      main: '#4A90E2',
      light: '#7BB3F0',
      dark: '#2E5C8A',
    },
    success: {
      main: '#5CB85C',
      light: '#7DC17D',
      dark: '#3D7C47',
    },
    warning: {
      main: '#F0AD4E',
      light: '#F5C971',
      dark: '#D58512',
    },
    error: {
      main: '#D9534F',
      light: '#E57373',
      dark: '#C62828',
    },
  },
  spacing: 8, // 8px base unit for consistent spacing
  shape: {
    borderRadius: 12, // Modern rounded corners
  },
  typography: {
    fontFamily: "'Inter', 'system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', sans-serif",
    h1: {
      fontFamily: "'Playfair Display', 'Georgia', serif",
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
      fontSize: '3.5rem',
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      fontFamily: "'Playfair Display', 'Georgia', serif",
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
      fontSize: '2.75rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h3: {
      fontFamily: "'Playfair Display', 'Georgia', serif",
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      fontSize: '2.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h4: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      fontSize: '1.5rem',
    },
    h5: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
      letterSpacing: '0em',
      lineHeight: 1.5,
      fontSize: '1.25rem',
    },
    h6: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
      letterSpacing: '0em',
      lineHeight: 1.5,
      fontSize: '1.125rem',
    },
    body1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      lineHeight: 1.6,
      fontSize: '1rem',
    },
    body2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      lineHeight: 1.5,
      fontSize: '0.875rem',
    },
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#0B0E26',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(201, 169, 97, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(201, 169, 97, 0.5)',
            },
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(201, 169, 97, 0.15)',
          backdropFilter: 'blur(8px)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 500,
          padding: '12px 24px',
          fontSize: '1rem',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #C9A961 0%, #E8D5A3 100%)',
          color: '#0B0E26',
          '&:hover': {
            background: 'linear-gradient(135deg, #E8D5A3 0%, #C9A961 100%)',
            boxShadow: '0 6px 20px rgba(201, 169, 97, 0.4)',
          },
        },
        outlined: {
          borderColor: 'rgba(201, 169, 97, 0.5)',
          color: '#C9A961',
          '&:hover': {
            borderColor: '#C9A961',
            backgroundColor: 'rgba(201, 169, 97, 0.1)',
          },
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1.125rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          height: 32,
          fontSize: '0.875rem',
        },
        colorPrimary: {
          backgroundColor: 'rgba(201, 169, 97, 0.2)',
          color: '#E8D5A3',
          border: '1px solid rgba(201, 169, 97, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(20, 24, 50, 0.8)',
          backdropFilter: 'blur(8px)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        },
        elevation4: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.15)',
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(11, 14, 38, 0.95)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          '@media (min-width: 600px)': {
            paddingLeft: 24,
            paddingRight: 24,
          },
        },
      },
    },
  },
});

export default bookishTheme; 